import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Loader2, ShieldCheck, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

const LEVEL_STYLES = {
  1: 'bg-[#F5EFE5] border-[#E9DDCC] text-[#7A6455]',
  2: 'bg-[#FBF3DC] border-[#DCC488] text-[#9A744C]',
  3: 'bg-[#EDF2EA] border-[#BCC9B6] text-[#5C7057]',
};

export default function DataQualityPanel() {
  const [preview, setPreview] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [internal, setInternal] = useState('');
  const [confirmRun, setConfirmRun] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, e, i] = await Promise.all([
        axios.get(`${API}/admin/data/cleanup-preview`, { withCredentials: true }),
        axios.get(`${API}/admin/data/engagement-overview`, { withCredentials: true }),
        axios.get(`${API}/admin/data/internal-accounts`, { withCredentials: true }),
      ]);
      setPreview(p.data);
      setEngagement(e.data);
      setInternal(i.data.emails.join('\n'));
    } catch (e) { toast.error('Impossible de charger la qualité des données.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runCleanup = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/admin/data/cleanup-run`, {}, { withCredentials: true });
      setResult(r.data);
      setConfirmRun(false);
      toast.success(r.data.actions.length ? `Nettoyage effectué — ${r.data.actions.length} action(s).` : 'Aucune action nécessaire — base déjà propre.');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors du nettoyage.'); }
    finally { setRunning(false); }
  };

  const setLevel = async (email, level) => {
    try {
      await axios.put(`${API}/admin/data/engagement`, { email, level: Number(level) }, { withCredentials: true });
      toast.success('Niveau d\'engagement mis à jour.');
      const e = await axios.get(`${API}/admin/data/engagement-overview`, { withCredentials: true });
      setEngagement(e.data);
    } catch (err) { toast.error('Erreur lors de la mise à jour.'); }
  };

  const saveInternal = async () => {
    try {
      const emails = internal.split('\n').map((e) => e.trim()).filter(Boolean);
      const r = await axios.put(`${API}/admin/data/internal-accounts`, { emails }, { withCredentials: true });
      setInternal(r.data.emails.join('\n'));
      toast.success('Comptes internes enregistrés — exclus des statistiques d\'engagement.');
    } catch (e) { toast.error('Erreur d\'enregistrement.'); }
  };

  if (!preview || !engagement) {
    return <Card className="border-[#E9DDCC] rounded-2xl"><CardContent className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#C99A55]" /></CardContent></Card>;
  }

  const nothingToDo = preview.to_delete.length === 0 && preview.to_merge.length === 0;

  return (
    <div className="space-y-5" data-testid="data-quality-panel">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-[#C99A55]" />
        <h3 className="font-heading text-lg text-[#493329]">Nettoyage & Qualité des données</h3>
      </div>

      {/* ===== Nettoyage A1 ===== */}
      <div className="bg-white rounded-2xl border border-[#E9DDCC] p-4 space-y-3" data-testid="dq-cleanup-block">
        <p className="text-[10px] uppercase tracking-wider text-[#7A6455] font-semibold flex items-center gap-1"><Trash2 className="w-3 h-3" /> Nettoyage de la base (suppressions & fusions validées)</p>
        {nothingToDo ? (
          <p className="text-sm text-[#5C7057]" data-testid="dq-cleanup-done">✓ Nettoyage déjà effectué — aucune action en attente.</p>
        ) : (
          <div className="text-xs text-[#7A6455] space-y-1">
            {preview.to_delete.map((d) => <div key={d.email}>• Suppression : <strong>{d.email}</strong>{d.name ? ` (${d.name})` : ''}</div>)}
            {preview.to_merge.map((m) => <div key={m.remove}>• Fusion : garder <strong>{m.keep}</strong>{m.keep_found ? '' : ' (⚠ introuvable)'} — supprimer <strong>{m.remove}</strong>{m.remove_name ? ` (${m.remove_name})` : ''}</div>)}
          </div>
        )}
        {!nothingToDo && (!confirmRun ? (
          <Button onClick={() => setConfirmRun(true)} className="bg-[#C99A55] hover:bg-[#493329] text-white rounded-xl h-10 text-sm" data-testid="dq-cleanup-run">
            Exécuter le nettoyage
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setConfirmRun(false)} disabled={running} className="rounded-xl h-10 text-sm border-[#E9DDCC]" data-testid="dq-cleanup-cancel">Annuler</Button>
            <Button onClick={runCleanup} disabled={running} className="bg-[#C76542] hover:bg-[#493329] text-white rounded-xl h-10 text-sm" data-testid="dq-cleanup-confirm">
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Oui, exécuter définitivement'}
            </Button>
          </div>
        ))}
        {result && (
          <div className="bg-[#F5EFE5] rounded-xl p-3 text-xs text-[#7A6455] space-y-1" data-testid="dq-cleanup-result">
            {result.actions.length === 0 && <p>Aucune action effectuée (déjà propre).</p>}
            {result.actions.map((a, i) => <p key={i}>✓ {a}</p>)}
            <p className="pt-1 text-[#493329]">
              <strong>Après nettoyage :</strong> {result.totals.preinscriptions} pré-inscriptions · {result.totals.confirmees} confirmées · {result.totals.non_confirmees} non confirmées · CA récurrent {result.totals.ca_recurrent} € · {result.totals.seances} séances · remplissage {result.totals.remplissage} %
            </p>
          </div>
        )}
      </div>

      {/* ===== Niveaux d'engagement A3 ===== */}
      <div className="bg-white rounded-2xl border border-[#E9DDCC] p-4 space-y-3" data-testid="dq-engagement-block">
        <p className="text-[10px] uppercase tracking-wider text-[#7A6455] font-semibold flex items-center gap-1"><Users className="w-3 h-3" /> Niveaux d'engagement (confirmées)</p>
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map((k) => (
            <div key={k} className={`rounded-xl border p-3 text-center ${LEVEL_STYLES[k]}`} data-testid={`dq-level-card-${k}`}>
              <div className="text-[10px] uppercase tracking-wider font-semibold">Niveau {k} — {engagement.levels[String(k)].label}</div>
              <div className="font-heading text-2xl text-[#493329]">{engagement.levels[String(k)].count}</div>
              <div className="text-[11px]">{Math.round(engagement.levels[String(k)].ca)} € / mois</div>
            </div>
          ))}
        </div>
        <p className="text-xs text-[#7A6455]">
          CA <strong>engagé</strong> (niveaux 2+3) : <strong className="text-[#493329]">{Math.round(engagement.ca_engage)} €</strong> · CA déclaré (niveau 1) : {Math.round(engagement.ca_declare)} €
        </p>

        <div className="max-h-[320px] overflow-y-auto divide-y divide-[#EFE4D0]">
          {engagement.clients.map((c) => (
            <div key={c.email} className="flex items-center justify-between gap-2 py-2 text-sm">
              <div className="min-w-0">
                <span className="text-[#493329]">{c.name}</span>
                <span className="block text-xs text-[#7A6455] truncate">{c.email} · {c.plan} · {c.ca} €</span>
              </div>
              <select
                value={c.level}
                onChange={(e) => setLevel(c.email, e.target.value)}
                className="text-xs border border-[#E9DDCC] rounded-lg px-2 py-1.5 bg-white text-[#493329]"
                data-testid={`dq-level-select-${c.email}`}
              >
                <option value={1}>1 — Déclarée</option>
                <option value={2}>2 — Engagée</option>
                <option value={3}>3 — Payée</option>
              </select>
            </div>
          ))}
          {engagement.clients.length === 0 && <p className="text-xs text-[#AE9885] py-2">Aucune cliente confirmée.</p>}
        </div>

        {engagement.relance.length > 0 && (
          <div className="bg-[#F3EADA] rounded-xl border border-[#C99A55]/40 p-3" data-testid="dq-relance-block">
            <p className="text-[10px] uppercase tracking-wider text-[#C99A55] font-semibold mb-1">À relancer pour engagement ({engagement.relance.length}) — triées par CA potentiel</p>
            {engagement.relance.slice(0, 10).map((r) => (
              <div key={r.email} className="text-xs text-[#7A6455] py-0.5">{r.name} · {r.plan} · {r.ca} €{r.phone ? ` · ${r.phone}` : ''}</div>
            ))}
          </div>
        )}
      </div>

      {/* ===== Comptes internes ===== */}
      <div className="bg-white rounded-2xl border border-[#E9DDCC] p-4 space-y-2" data-testid="dq-internal-block">
        <p className="text-[10px] uppercase tracking-wider text-[#7A6455] font-semibold">Comptes internes / test (exclus des statistiques d'engagement)</p>
        <Textarea value={internal} onChange={(e) => setInternal(e.target.value)} placeholder="un email par ligne" className="text-xs rounded-xl border-[#E9DDCC] min-h-[70px]" data-testid="dq-internal-textarea" />
        <Button onClick={saveInternal} variant="outline" className="rounded-xl h-9 text-xs border-[#E9DDCC]" data-testid="dq-internal-save">Enregistrer la liste</Button>
      </div>
    </div>
  );
}
